import { gapForecastDayWords, sailingGapForecastScore, type GapForecastInfo } from "./gap-forecast-display";
import type { SailingOutlookDay } from "./detail";
import { scoreRowStateAt } from "./score-freshness";
import { offsetIsoDate, spotLocalDate } from "./spot-locality";

// The sailing page's score freshness at one instant. The page is statically rendered and rebuilt
// every 3 hours, so this runs in the browser on its own clock (lib/hooks/use-minute-clock.ts):
// the shared rule (score-freshness.ts scoreRowStateAt) on the detail's own row date and write,
// with the venue's zone. Pure and light, so client components can import it. Everything that is
// formatted with a time zone name (the issue time) is formatted on the server and passed in, so
// the server and browser render the same text.

export type SailingFreshnessInput = Readonly<{
  forecastDate: string;
  lastUpdated: string;
  timezone: string | null;
  longitude: number;
  /** "Forecast issued 21:40 BST", formatted on the server (gapForecastIssuedLabel). */
  gapIssuedLabel: string;
  /** The row holds a usable forecast for the day after its date in at least one category. */
  gapForecastAvailable: boolean;
}>;

export type SailingFreshness = Readonly<{
  /** The stored row is not today's current score (stale, or the nightly gap). */
  stale: boolean;
  /** The venue's local date at that instant (the outlook starts here). */
  todayISO: string;
  /** The nightly gap, when a category has a forecast for today: shown labelled as such. */
  gapForecast: GapForecastInfo | null;
}>;

/** Whether the row's outlook holds a usable gap forecast for the day after its date (server side). */
export function sailingGapForecastAvailable(forecastDate: string, forecast: readonly SailingOutlookDay[]): boolean {
  const next = offsetIsoDate(forecastDate, 1);
  const day = forecast.find((entry) => entry.date === next);
  return sailingGapForecastScore(day, "club") !== null || sailingGapForecastScore(day, "hp") !== null;
}

export function sailingFreshnessAt(input: SailingFreshnessInput, nowMs: number): SailingFreshness {
  const locality = { timezone: input.timezone, longitude: input.longitude };
  const state = scoreRowStateAt({ date: input.forecastDate, updatedAt: input.lastUpdated }, locality, nowMs);
  return {
    stale: state.kind !== "current",
    todayISO: spotLocalDate(new Date(nowMs), locality),
    gapForecast: state.kind === "forecast" && input.gapForecastAvailable
      ? { date: state.date, issuedAt: new Date(state.issuedAtMs).toISOString(), issuedLabel: input.gapIssuedLabel }
      : null,
  };
}

/**
 * The category card's heading: "Today's score" for a stale row (which reads "Unavailable") or, after
 * hydration, a current one; before hydration (the static HTML and the first client render) a current
 * row is named by its own date, "Score for Thursday 1 October", because that HTML can be served
 * hours after it was built and after the venue's midnight.
 */
export function sailingScoreHeading(input: Readonly<{ stale: boolean; hydrated: boolean; forecastDate: string }>): string {
  if (input.stale || input.hydrated) return "Today’s score";
  return `Score for ${gapForecastDayWords(input.forecastDate)}`;
}
